import { useFormikEnhanced } from '@superdispatch/forms';
import { Column, Columns, useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { APIError } from 'shared/api/APIError';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useInitialValues } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { ProfileTransporterPlateRegistrationForm } from 'shared/modules/carrier-profile/core/ProfileTransporterPlateRegistrationForm';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';
import {
  useCarrierProfileAPI,
  useTransporterPlateRegistration,
} from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import {
  TransporterPlateRegistrationDTO,
  transporterPlateRegistrationSchema,
  transporterPlateRegistrationYupSchema,
} from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import {
  useSubmitVerificationDocument,
  VerificationDialogs,
} from './core/VerificationDialogs';
import { ProfilePageLayout } from './ProfilePageLayout';
import { useEffect, useMemo } from 'react';

export function ProfileTransporterPlateRegistrationPage() {
  const { addSnackbar } = useSnackbarStack();
  const { updateTransporterPlateRegistration } = useCarrierProfileAPI();
  const { data, isLoading, isError } = useTransporterPlateRegistration();

  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    transporterPlateRegistrationYupSchema,
    transporterPlateRegistrationSchema,
  );
  const initialValues = useInitialValues(schema, data);
  const validationSchema = useMemo(
    () => toFormikValidationSchema(schema),
    [schema],
  );

  const formik = useFormikEnhanced<
    TransporterPlateRegistrationDTO,
    APIResponse
  >({
    initialValues,
    validationSchema,
    onSubmit: updateTransporterPlateRegistration,
    onSubmitSuccess: () => {
      addSnackbar('Transporter Plate Registration was successfully updated', {
        variant: 'success',
      });

      trackCarrierProfileEvent({
        name: 'Carrier Updated Carrier Info',
        utm_medium: 'Carrier Profile',
        utm_content: 'Transporter Plate Registration',
      });
    },
    onSubmitFailure: ({ message }: APIError) => {
      addSnackbar(
        `Failed to update Transporter Plate Registration: ${message}`,
        {
          variant: 'error',
        },
      );
    },
  });

  const submitVerificationForm = useSubmitVerificationDocument(formik);

  useEffect(() => {
    if (isError) {
      addSnackbar('Failed to fetch Transporter Plate Registration', {
        variant: 'error',
      });
    }
  }, [isError, addSnackbar]);

  return (
    <>
      <DocumentTitle title="Transporter Plate Registration - Profile" />

      <VerificationDialogs formik={formik} />

      <ProfilePageLayout
        formik={formik}
        title="Transporter Plate Registration"
        isLoading={isLoading}
        actions={
          <Button
            type="button"
            onClick={submitVerificationForm}
            pending={formik.isSubmitting}
            disabled={!formik.dirty}
            fullWidth={isWebView}
          >
            Save
          </Button>
        }
      >
        <Columns space="xxlarge" collapseBelow="desktop">
          <Column>
            <ProfileTransporterPlateRegistrationForm />
          </Column>
        </Columns>
      </ProfilePageLayout>
    </>
  );
}
